import React from 'react';
import routes from '../Config/RoutesFE';
import ERROR_CODES from '../Config/ErrorCodes';
import logger from '../utils/Logger';

const SubscriptionExpiredModal = ({ show, onHide, modalType = 'subscription_expired' }) => {

  // Handler for renewing subscription
  const handleRenewSubscription = () => {
    onHide();
    // Navigate to plans page using route from RoutesFE
    window.location.href = routes.PLANS;
  };

  // Handler for sign up
  const handleSignUp = () => {
    onHide();
    // Navigate to sign up page
    window.location.href = routes.SIGNUP || '/signup';
  };

  // Handler for verify email
  const handleVerifyEmail = () => {
    onHide();
    // Navigate to verify email page or show verification instruction
    window.location.href = routes.VERIFY_EMAIL || '/verify-email';
  };

  // Define content based on modal type using switch case
  const getModalContent = () => {
    switch (modalType) {
      case 'user_identity_not_found':
        return {
          title: 'Sign Up Required',
          icon: 'bi-person-plus-fill',
          message: 'User identity not found. Please sign up to continue.',
          description: '',
          showAction: true,
          actionLabel: 'Sign Up',
          actionHandler: handleSignUp,
          showSecondaryAction: true
        };

      case 'account_disabled':
        return {
          title: 'Account Disabled',
          icon: 'bi-exclamation-octagon-fill',
          message: 'Your account is disabled. Please contact support.',
          description: '',
          showAction: false,
          actionLabel: '',
          actionHandler: null,
          showSecondaryAction: true
        };

      case 'account_not_confirmed':
        return {
          title: 'Email Verification Required',
          icon: 'bi-envelope-exclamation-fill',
          message: 'Your account is not confirmed. Please verify your email.',
          description: '',
          showAction: true,
          actionLabel: 'Verify Email',
          actionHandler: handleVerifyEmail,
          showSecondaryAction: true
        };

      case 'no_active_subscription':
        return {
          title: 'No Active Subscription',
          icon: 'bi-calendar-x-fill',
          message: 'No active subscription found. Please upgrade to continue.',
          description: '',
          showAction: true,
          actionLabel: 'Upgrade Now',
          actionHandler: handleRenewSubscription,
          showSecondaryAction: true
        };

      case 'subscription_expired':
        return {
          title: 'Subscription Expired',
          icon: 'bi-exclamation-triangle-fill',
          message: 'Your subscription plan has expired. Please renew to continue.',
          description: '',
          showAction: true,
          actionLabel: 'Renew Subscription',
          actionHandler: handleRenewSubscription,
          showSecondaryAction: true
        };

      case 'plan_limit':
        return {
          title: 'Plan Limit Reached',
          icon: 'bi-exclamation-circle-fill',
          message: 'You have reached your plan limit. Upgrade or renew to continue.',
          description: '',
          showAction: true,
          actionLabel: 'Upgrade Now',
          actionHandler: handleRenewSubscription,
          showSecondaryAction: true
        };

      case 'trial_expired':
        return {
          title: 'Free Trial Expired',
          icon: 'bi-clock-history',
          message: 'Your free trial has ended. Please upgrade to a paid plan to continue recording.',
          description: '',
          showAction: true,
          actionLabel: 'Upgrade Now',
          actionHandler: handleRenewSubscription,
          showSecondaryAction: true
        };

      case 'server_error':
        return {
          title: 'Server Error',
          icon: 'bi-exclamation-triangle-fill',
          message: 'Server is down. Please try again after some time.',
          description: '',
          showAction: false,
          showSecondaryAction: true
        };

      default:
        return {
          title: 'Error',
          icon: 'bi-exclamation-triangle-fill',
          message: 'An error occurred. Please try again later.',
          description: '',
          showAction: false,
          showSecondaryAction: true
        };
    }
  };

  const content = getModalContent();

  if (!show) return null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
        <div className="modal-content">
          <div className="modal-header bg-primary text-white py-2">
            <h6 className="modal-title d-flex align-items-center justify-content-center mb-0 w-100">
              <i className={`${content.icon} me-2`}></i>
              {content.title}
            </h6>
          </div>
          
          <div className="modal-body text-center py-4">
            <div className="d-flex flex-column align-items-center justify-content-center">
              <i className={`${content.icon} text-primary mb-3`} style={{ fontSize: '2rem' }}></i>
              <h6 className="mb-0 text-dark text-center">{content.message}</h6>
            </div>
          </div>
          
          <div className="modal-footer justify-content-center py-1">
            {content.showAction && (
              <button 
                type="button"
                className="btn btn-primary btn-sm px-3 me-2"
                onClick={content.actionHandler}
              >
                <i className={`bi ${
                  modalType === 'user_identity_not_found' ? 'bi-person-plus' :
                  modalType === 'account_disabled' ? 'bi-telephone' :
                  modalType === 'account_not_confirmed' ? 'bi-envelope-check' :
                  modalType === 'no_active_subscription' ? 'bi-credit-card' :
                  'bi-credit-card'
                } me-1`}></i>
                {content.actionLabel}
              </button>
            )}
            {content.showSecondaryAction && (
              <button 
                type="button"
                className="btn btn-outline-secondary btn-sm px-3"
                onClick={onHide}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the helper function for use in other components
// This function maps API error messages/codes to modal types using a switch statement
export const checkSubscriptionExpired = (responseData, setShowModal) => {
  
  // Check for error codes first (preferred method)
  if (responseData && responseData.code) {
    const errorCode = responseData.code;
    
    switch (errorCode) {
      case ERROR_CODES.NO_ACTIVE_PLAN:
        setShowModal('no_active_subscription');
        return true;
      
      case ERROR_CODES.PLAN_LIMIT_REACHED:
        setShowModal('plan_limit');
        return true;
      
      default:
        logger.warn('Unrecognized error code received', {
          component: 'SubscriptionExpiredModal',
          action: 'checkSubscriptionExpired',
          errorCode
        });
        break;
    }
  }
  
  if (responseData && responseData.error) {
    const errorMessage = responseData.error;

    // Use switch case to handle different error messages
    switch (true) {
      // User identity not found
      case errorMessage === 'User identity not found.':
        setShowModal('user_identity_not_found');
        return true;

      // Account disabled
      case errorMessage === 'Your account is disabled. Please contact support.':
        setShowModal('account_disabled');
        return true;

      // Account not confirmed
      case errorMessage === 'Your account is not confirmed. Please verify your email.':
        setShowModal('account_not_confirmed');
        return true;

      // No active subscription (both variations)
      case errorMessage === 'No active subscription.' ||
           errorMessage === 'No active subscription found for this user.':
        setShowModal('no_active_subscription');
        return true;

      // Subscription expired
      case errorMessage.includes('subscription plan has expired') ||
           errorMessage === 'Your subscription plan has expired. Please renew to continue.':
        setShowModal('subscription_expired');
        return true;

      // Plan limit reached
      case errorMessage === 'You have reached your plan limit. Upgrade or renew to continue.':
        setShowModal('plan_limit');
        return true;

      // Free trial expired
      case errorMessage === 'Your free trial has expired. Please upgrade to a paid plan.' ||
           errorMessage === 'Your free trial usage limit has been reached. Please upgrade to a paid plan.' ||
           errorMessage.includes('free trial has expired') ||
           errorMessage.includes('free trial usage limit'):
        setShowModal('trial_expired');
        return true;

      default:
        return false;
    }
  }

  // Check for HTTP 403 Forbidden status
  if (responseData && responseData.status === 403) {
    setShowModal('server_error');
    return true;
  }
  
  return false;
};

export default SubscriptionExpiredModal;
